import * as React from "react";
import { useParams } from "react-router";

import { Container, FormControl, Grid, MenuItem, Select, Stack, ToggleButton, ToggleButtonGroup, useMediaQuery } from "@mui/material";

import TextFieldsIcon from "@mui/icons-material/TextFields";
import TableChartIcon from "@mui/icons-material/TableChart";
import InsertChartIcon from "@mui/icons-material/InsertChart";
import PieChartIcon from "@mui/icons-material/PieChart";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";

import CustomForm from "../../../helper-components/form/CustomForm.jsx";
import NavigationFooter from "../../../helper-components/misc/NavigationFooter.jsx";
import PaperText from "../../../helper-components/misc/PaperText.jsx";
import CustomButton from "../../../helper-components/input/CustomButton.jsx";

import ExaminationViewChart from "./components/ExaminationViewChart.jsx";
import ExaminationViewTable from "./components/ExaminationViewTable.jsx";
import PlaqueAndBleedingChart from "./components/PlaqueAndBleedingChart.jsx";
import ExaminationEditMenu from "./components/ExaminationEditMenu.jsx";

import { formatDate, isEmpty } from "../../../helper-functions/shared/miscUtils.js";
import { getPatientDisplayName } from "../../../helper-functions/shared/patientDisplay.js";
import { queryExamination } from "../../../helper-functions/api/examinationQueries.js";

import { getLanguage } from "../../../config/language/language.js";

export default function ExaminationView(props) {
    const { id } = useParams(); // Get the URL parameters from react-router.
    const isMobile = useMediaQuery("@media (max-width:630px)");

    const [examination, setExamination] = React.useState({});
    const [examinationQuery, setExaminationQuery] = React.useState(false);

    const [viewMode, setViewMode] = React.useState("chart"); // 'table' | 'chart' | 'pdbop'

    const examinations = props?.backend?.examinations?.data;

    const isReady = !isEmpty(examination) && !isEmpty(examination?.teeth_data);

    const openPatientCompare = () => {
        if (!examination?.patient_id) return;
        props.browser.navigate(`/database/patients/${examination.patient_id}/compare?followup=${examination.id}&mode=diff`);
    };

    // Set the examination, and potentially query it if it's not available.
    React.useEffect(() => {
        const possibleExamination = examinations.find((t) => t.id === parseInt(id));

        if (!isEmpty(possibleExamination)) {
            setExaminationQuery(true);
            setExamination(possibleExamination);
        }

        if (isEmpty(possibleExamination) && !examinationQuery) {
            setExaminationQuery(true);
            queryExamination(props, { id });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [examinations, examination]);

    return (
        <Container maxWidth={viewMode === "table" ? false : "xl"} sx={{ mt: 2, mb: 2 }}>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <CustomForm
                        title={getLanguage().EXAMINATION_VIEW_TITLE}
                        subtitle={getLanguage().EXAMINATION_VIEW_SUBTITLE}
                        icon={<TextFieldsIcon />}
                        form={
                            <Stack
                                direction="row"
                                spacing={1}
                                useFlexGap
                                alignItems="center"
                                justifyContent="center"
                                sx={{ flexWrap: "wrap", width: "100%", mt: 2 }}>
                                    {isMobile ? (
                                        <FormControl size="small" sx={{ minWidth: 160 }}>
                                            <Select value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
                                                <MenuItem value="table">
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <TableChartIcon fontSize="small" />
                                                        <span>Table View</span>
                                                    </Stack>
                                                </MenuItem>
                                                <MenuItem value="chart">
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <InsertChartIcon fontSize="small" />
                                                        <span>Chart View</span>
                                                    </Stack>
                                                </MenuItem>
                                                <MenuItem value="pdbop">
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <PieChartIcon fontSize="small" />
                                                        <span>PI/BOP/PD</span>
                                                    </Stack>
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <ToggleButtonGroup
                                            value={viewMode}
                                            exclusive
                                            onChange={(e, newMode) => newMode && setViewMode(newMode)}
                                            size="small"
                                            color="primary"
                                            sx={{ flexWrap: "wrap", justifyContent: "center", maxWidth: "100%" }}>
                                            <ToggleButton value="table">
                                                <TableChartIcon sx={{ mr: 0.5 }} />
                                                Table View
                                            </ToggleButton>
                                            <ToggleButton value="chart">
                                                <InsertChartIcon sx={{ mr: 0.5 }} />
                                                Chart View
                                            </ToggleButton>
                                            <ToggleButton value="pdbop">
                                                <PieChartIcon sx={{ mr: 0.5 }} />
                                                PI/BOP/PD
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    )}

                                    {!isMobile && (
                                        <CustomButton {...props} title="Compare examinations" size="small" startIcon={<CompareArrowsIcon />} onClick={openPatientCompare} />
                                    )}
                                    <ExaminationEditMenu
                                        {...props}
                                        examination={examination}
                                        onPatientUpdated={(patient) => setExamination((current) => ({ ...current, patient }))}
                                    />
                            </Stack>
                        }
                    />
                </Grid>

                {!examinationQuery && !isReady && (
                    <Grid item xs={12}>
                        <PaperText value={`Loading, please wait...`} center p={8} />
                    </Grid>
                )}

                {examinationQuery && !isReady && (
                    <Grid item xs={12}>
                        <PaperText value={getLanguage().NO_EXAMINATION_FOUND} />
                    </Grid>
                )}

                {isReady && viewMode === "table" && (
                    <Grid container item xs={12}>
                        <ExaminationViewTable
                            teethData={examination.teeth_data}
                            summaryData={examination.summary_data}
                            patientInfo={{
                                name: getPatientDisplayName(examination?.patient),
                                date: formatDate(examination?.created_on),
                                createdOn: examination?.created_on,
                                patient: examination?.patient
                            }}
                            openAppDrawer={props.sidebar.openAppDrawer}
                        />
                    </Grid>
                )}

                {isReady && viewMode === "chart" && (
                    <Grid item xs={12}>
                        <ExaminationViewChart
                            teethData={examination.teeth_data}
                            summaryData={examination.summary_data}
                            patientInfo={{
                                name: getPatientDisplayName(examination?.patient),
                                date: formatDate(examination?.created_on),
                                createdOn: examination?.created_on,
                                patient: examination?.patient
                            }}
                            openAppDrawer={props.sidebar.openAppDrawer}
                        />
                    </Grid>
                )}

                {isReady && viewMode === "pdbop" && (
                    <Grid item xs={12}>
                        <PlaqueAndBleedingChart
                            teethData={examination.teeth_data}
                            patientInfo={{
                                name: getPatientDisplayName(examination?.patient),
                                date: formatDate(examination?.created_on, { onlyDate: true })
                            }}
                        />
                    </Grid>
                )}

                <NavigationFooter
                    rows={1}
                    options={[
                        {
                            path: `/database/examinations`,
                            title: "Back to examinations",
                            variant: "outlined",
                            icon: <TextFieldsIcon />
                        },
                        {
                            title: "Compare examinations",
                            variant: "outlined",
                            icon: <CompareArrowsIcon />,
                            onClick: openPatientCompare,
                            disabled: !examination?.patient_id
                        }
                    ]}
                />
            </Grid>
        </Container>
    );
}
